/**
 * CinematicManager
 *
 * Manages cinematic sequences including camera animations and timeline events.
 * Camera animations are loaded from map GLB files.
 * Events are processed at normalized time using the hook system.
 */

import { AnimationGroup, Camera, Scene, Vector3 } from '@babylonjs/core';
import type { AssetContainer } from '@babylonjs/core';
import type { CinematicSequence, CinematicEvent } from '../types/gameplay';
import type { CinematicHookAPI } from '../behaviors/types/cinematic';
import { CinematicHookRegistry } from '../behaviors/hooks/CinematicHookRegistry';
import { registerCinematicPresets } from '../behaviors/presets/cinematic';
import { logger } from '../core/Logger';
import type { CameraController } from './CameraController';

/** Active cinematic state */
interface ActiveCinematic {
  sequence: CinematicSequence;
  animGroup: AnimationGroup | null;
  processedEvents: Set<CinematicEvent>;
  startFrame: number;
  endFrame: number;
  startTime: number;
  duration: number;
}

/** Callback types */
export type CinematicEventCallback = (eventName: string, data?: unknown) => void;
export type PlayerInputCallback = (enabled: boolean) => void;
export type CinematicCompleteCallback = () => void;

class CinematicManagerImpl {
  private scene: Scene | null = null;
  private cameraAnimations: Map<string, AnimationGroup> = new Map();
  private activeSequence: ActiveCinematic | null = null;
  private cinematicCamera: Camera | null = null;
  private presetsRegistered = false;
  
  // Camera controller integration
  private cameraController: CameraController | null = null;

  // External callbacks
  private onEventEmit: CinematicEventCallback | null = null;
  private onPlayerInput: PlayerInputCallback | null = null;
  private onComplete: CinematicCompleteCallback | null = null;

  /**
   * Initialize the cinematic manager
   */
  initialize(scene: Scene, cameraController?: CameraController): void {
    this.scene = scene;
    this.cameraController = cameraController ?? null;

    // Register built-in presets once
    if (!this.presetsRegistered) {
      registerCinematicPresets();
      this.presetsRegistered = true;
    }

    logger.core.info('CinematicManager: Initialized');
  }

  /**
   * Set callback for emitted events
   */
  setEventCallback(callback: CinematicEventCallback): void {
    this.onEventEmit = callback;
  }

  /**
   * Set callback for player input changes
   */
  setPlayerInputCallback(callback: PlayerInputCallback): void {
    this.onPlayerInput = callback;
  }

  /**
   * Set callback for when cinematic completes
   */
  setCompleteCallback(callback: CinematicCompleteCallback): void {
    this.onComplete = callback;
  }

  /**
   * Load camera animations from a GLB asset container
   */
  loadFromGLB(container: AssetContainer): void {
    let loadedCount = 0;

    for (const animGroup of container.animationGroups) {
      // Check if this animation targets a camera
      const hasCameraTarget = animGroup.targetedAnimations.some((ta) => {
        const target = ta.target;
        return (
          target instanceof Camera ||
          (target && typeof target === 'object' && 'name' in target &&
            typeof target.name === 'string' && target.name.toLowerCase().includes('camera'))
        );
      });

      if (hasCameraTarget) {
        this.cameraAnimations.set(animGroup.name, animGroup);
        loadedCount++;
        logger.core.debug(`CinematicManager: Loaded camera animation "${animGroup.name}"`);
      }
    }

    if (loadedCount > 0) {
      logger.core.info(`CinematicManager: Loaded ${loadedCount} camera animations from GLB`);
    }
  }

  /**
   * Register camera animations directly (already filtered from map load)
   */
  registerAnimations(animGroups: AnimationGroup[]): void {
    for (const animGroup of animGroups) {
      this.cameraAnimations.set(animGroup.name, animGroup);
      logger.core.debug(`CinematicManager: Registered camera animation "${animGroup.name}"`);
    }
    if (animGroups.length > 0) {
      logger.core.info(`CinematicManager: Registered ${animGroups.length} camera animations`);
    }
  }

  /**
   * Get list of available camera animation names
   */
  getAvailableAnimations(): string[] {
    return Array.from(this.cameraAnimations.keys());
  }

  /**
   * Check if a camera animation exists
   */
  hasAnimation(name: string): boolean {
    return this.cameraAnimations.has(name);
  }

  /**
   * Play a startup cinematic by animation name
   * Convenience method for simple camera animation playback
   */
  async playStartupAnimation(animationName: string, blendOutDuration: number = 1.0): Promise<void> {
    if (!animationName) return;

    const sequence: CinematicSequence = {
      id: `startup_${animationName}`,
      cameraAnimationName: animationName,
      events: [],
      blendInDuration: 0,  // Start immediately
      blendOutDuration: blendOutDuration
    };

    return this.play(sequence);
  }

  /**
   * Play a cinematic sequence
   */
  async play(sequence: CinematicSequence): Promise<void> {
    if (!this.scene) {
      throw new Error('CinematicManager: Not initialized');
    }

    // Stop any current sequence
    if (this.activeSequence) {
      this.stop();
    }

    // Find camera animation
    const animGroup = this.cameraAnimations.get(sequence.cameraAnimationName);
    
    let startFrame = 0;
    let endFrame = 0;
    let duration = 2; // Default duration if no animation

    if (animGroup) {
      startFrame = animGroup.from;
      endFrame = animGroup.to;
      // Estimate duration from frame range (assuming 30fps)
      duration = (endFrame - startFrame) / 30;
    } else if (sequence.cameraAnimationName) {
      logger.core.warn(
        `CinematicManager: Camera animation not found: "${sequence.cameraAnimationName}"`
      );
    }

    this.activeSequence = {
      sequence,
      animGroup: animGroup ?? null,
      processedEvents: new Set(),
      startFrame,
      endFrame,
      startTime: performance.now(),
      duration
    };

    logger.core.info(
      `CinematicManager: Playing sequence "${sequence.id}" (duration: ${duration.toFixed(1)}s)`
    );

    // Disable player input during cinematic
    this.onPlayerInput?.(false);

    // Enter cinematic mode on camera controller
    if (this.cameraController) {
      this.cameraController.enterCinematicMode();
    }

    // Start animation if available
    if (animGroup) {
      animGroup.start(false, 1.0, startFrame, endFrame);
    }

    // Wait for completion
    return new Promise((resolve) => {
      if (animGroup) {
        animGroup.onAnimationGroupEndObservable.addOnce(() => {
          this.onSequenceComplete();
          resolve();
        });
      } else {
        // No animation - use timer based on events
        const maxEventTime = sequence.events.reduce((max, e) => Math.max(max, e.time), 0);
        const waitDuration = Math.max(1, maxEventTime * duration);

        setTimeout(() => {
          this.onSequenceComplete();
          resolve();
        }, waitDuration * 1000);
      }
    });
  }

  /**
   * Stop the current cinematic
   */
  stop(): void {
    if (!this.activeSequence) return;

    const { animGroup } = this.activeSequence;
    if (animGroup) {
      animGroup.stop();
    }

    logger.core.info('CinematicManager: Sequence stopped');
    this.activeSequence = null;
  }

  /**
   * Update cinematic (call each frame)
   */
  update(_deltaTime: number): void {
    if (!this.activeSequence || !this.scene) return;

    const { sequence, animGroup, processedEvents, startFrame, endFrame, startTime, duration } =
      this.activeSequence;

    // Calculate normalized time
    let normalizedTime: number;

    if (animGroup && animGroup.animatables.length > 0) {
      const currentFrame = animGroup.animatables[0].masterFrame ?? startFrame;
      normalizedTime = (currentFrame - startFrame) / Math.max(1, endFrame - startFrame);
    } else {
      // Fallback to wall-clock time
      const elapsed = (performance.now() - startTime) / 1000;
      normalizedTime = Math.min(1, elapsed / duration);
    }

    // Process events
    const api = this.createHookAPI(normalizedTime);

    for (const event of sequence.events) {
      if (normalizedTime >= event.time && !processedEvents.has(event)) {
        logger.core.debug(
          `CinematicManager: Firing event "${event.hook}" at t=${normalizedTime.toFixed(2)}`
        );
        CinematicHookRegistry.invoke(event.hook, api, event.params ?? {});
        processedEvents.add(event);
      }
    }
  }

  /**
   * Check if a cinematic is playing
   */
  isPlaying(): boolean {
    return this.activeSequence !== null;
  }

  /**
   * Get current normalized time (0-1)
   */
  getNormalizedTime(): number {
    if (!this.activeSequence) return 0;

    const { animGroup, startFrame, endFrame, startTime, duration } = this.activeSequence;

    if (animGroup && animGroup.animatables.length > 0) {
      const currentFrame = animGroup.animatables[0].masterFrame ?? startFrame;
      return (currentFrame - startFrame) / Math.max(1, endFrame - startFrame);
    }

    const elapsed = (performance.now() - startTime) / 1000;
    return Math.min(1, elapsed / duration);
  }

  private onSequenceComplete(): void {
    if (!this.activeSequence) return;

    const { sequence } = this.activeSequence;

    logger.core.info(`CinematicManager: Sequence "${sequence.id}" complete`);

    // Exit cinematic mode with blend-out
    if (this.cameraController) {
      this.cameraController.exitCinematicMode(sequence.blendOutDuration ?? 1.0);
    }

    // Re-enable player input
    this.onPlayerInput?.(true);

    this.activeSequence = null;

    // Notify completion callback
    this.onComplete?.();
  }

  private createHookAPI(normalizedTime: number): CinematicHookAPI {
    const sequence = this.activeSequence!.sequence;
    const scene = this.scene!;
    const camera = this.cinematicCamera;

    return {
      sequence,
      normalizedTime,
      camera,
      scene,
      actions: {
        emit: (eventName: string, data?: unknown) => {
          this.onEventEmit?.(eventName, data);
        },
        spawnEntity: (behaviorId: string, position: Vector3) => {
          // TODO: Implement entity spawning via behavior system
          logger.core.debug(`CinematicManager: Spawn ${behaviorId} at ${position}`);
        },
        playSound: (soundId: string, _options?: { volume?: number }) => {
          // TODO: Implement sound playback
          logger.core.debug(`CinematicManager: Play sound ${soundId}`);
        },
        triggerBehavior: (triggerId: string) => {
          // TODO: Implement trigger invocation
          logger.core.debug(`CinematicManager: Trigger ${triggerId}`);
        },
        setPlayerInput: (enabled: boolean) => {
          this.onPlayerInput?.(enabled);
        },
        fadeScreen: (targetAlpha: number, durationSeconds: number) => {
          // TODO: Implement screen fade via post-process
          logger.core.debug(`CinematicManager: Fade to ${targetAlpha} over ${durationSeconds}s`);
        }
      }
    };
  }
  
  /**
   * Clear all camera animations and stop any active sequence.
   * Call this on level unload to prevent references to disposed cameras.
   */
  clear(): void {
    this.stop();
    this.cameraAnimations.clear();
    this.cinematicCamera = null;
    logger.core.info('CinematicManager: Cleared all camera animations');
  }
}

// Singleton instance
export const CinematicManager = new CinematicManagerImpl();
